/**
 * Service for managing character-specific rulebook preferences
 * Handles synchronization between global and character-specific settings
 */

import { Character } from '../types/character';
import { rulebookService } from './RulebookService';

export class CharacterRulebookService {
  private static instance: CharacterRulebookService;

  private constructor() {}

  public static getInstance(): CharacterRulebookService {
    if (!CharacterRulebookService.instance) {
      CharacterRulebookService.instance = new CharacterRulebookService();
    }
    return CharacterRulebookService.instance;
  }

  /**
   * Initialize a new character with current global rulebook settings
   */
  public initializeCharacterRulebooks(character: Character): Character {
    const globalEnabledRulebooks = rulebookService.getEnabledRulebookIds();

    return {
      ...character,
      enabledRulebooks: [...globalEnabledRulebooks]
    };
  }

  /**
   * Sync character rulebooks with global settings
   * Preserves character-specific overrides while adding newly enabled global rulebooks
   */
  public syncWithGlobalSettings(character: Character): Character {
    const globalEnabledRulebooks = rulebookService.getEnabledRulebookIds();
    const characterRulebooks = character.enabledRulebooks || [];

    // Merge global and character settings
    // Add any globally enabled rulebooks that aren't in character settings
    const mergedRulebooks = new Set([...characterRulebooks]);

    globalEnabledRulebooks.forEach((rulebookId) => {
      mergedRulebooks.add(rulebookId);
    });

    return {
      ...character,
      enabledRulebooks: Array.from(mergedRulebooks),
      lastModified: new Date()
    };
  }

  /**
   * Update character's rulebook preferences
   * This allows per-character customization independent of global settings
   */
  public updateCharacterRulebooks(character: Character, enabledRulebooks: string[]): Character {
    // Ensure core is always included
    const rulebooksWithCore = enabledRulebooks.includes('rogue-trader-core')
      ? enabledRulebooks
      : ['rogue-trader-core', ...enabledRulebooks];

    return {
      ...character,
      enabledRulebooks: rulebooksWithCore,
      lastModified: new Date()
    };
  }

  /**
   * Get unavailable content for a character based on their rulebook settings
   */
  public getUnavailableContent(character: Character): {
    hasUnavailableContent: boolean;
    unavailableItems: {
      career: boolean;
      homeworld: boolean;
      birthright: boolean;
      lure: boolean;
      trials: boolean;
      motivation: boolean;
      skills: string[];
      talents: string[];
      traits: string[];
      equipment: string[];
      weapons: string[];
      armor: string[];
    };
  } {
    const characterRulebooks = character.enabledRulebooks || [];

    const checkAvailability = (item: { rulebook: string }) => {
      return characterRulebooks.includes(item.rulebook);
    };

    const unavailableItems = {
      career: character.career.id && !checkAvailability(character.career),
      homeworld: character.homeworld.id && !checkAvailability(character.homeworld),
      birthright: character.birthright.id && !checkAvailability(character.birthright),
      lure: character.lure.id && !checkAvailability(character.lure),
      trials: character.trials.id && !checkAvailability(character.trials),
      motivation: character.motivation.id && !checkAvailability(character.motivation),
      skills: character.skills.filter((item) => !checkAvailability(item)).map((item) => item.id),
      talents: character.talents.filter((item) => !checkAvailability(item)).map((item) => item.id),
      traits: character.traits.filter((item) => !checkAvailability(item)).map((item) => item.id),
      equipment: character.equipment
        .filter((item) => !checkAvailability(item))
        .map((item) => item.id),
      weapons: character.weapons.filter((item) => !checkAvailability(item)).map((item) => item.id),
      armor: character.armor.filter((item) => !checkAvailability(item)).map((item) => item.id)
    };

    const hasUnavailableContent =
      unavailableItems.career ||
      unavailableItems.homeworld ||
      unavailableItems.birthright ||
      unavailableItems.lure ||
      unavailableItems.trials ||
      unavailableItems.motivation ||
      unavailableItems.skills.length > 0 ||
      unavailableItems.talents.length > 0 ||
      unavailableItems.traits.length > 0 ||
      unavailableItems.equipment.length > 0 ||
      unavailableItems.weapons.length > 0 ||
      unavailableItems.armor.length > 0;

    return {
      hasUnavailableContent,
      unavailableItems
    };
  }

  /**
   * Validate character against their enabled rulebooks
   */
  public validateCharacterRulebooks(character: Character): {
    isValid: boolean;
    warnings: string[];
    suggestions: string[];
  } {
    const { hasUnavailableContent, unavailableItems } = this.getUnavailableContent(character);
    const warnings: string[] = [];
    const suggestions: string[] = [];

    if (hasUnavailableContent) {
      if (unavailableItems.career) {
        warnings.push(`Career "${character.career.name}" is from a disabled rulebook`);
        suggestions.push(`Enable the "${character.career.rulebook}" rulebook to use this career`);
      }

      if (unavailableItems.homeworld) {
        warnings.push(`Homeworld "${character.homeworld.name}" is from a disabled rulebook`);
        suggestions.push(
          `Enable the "${character.homeworld.rulebook}" rulebook to use this homeworld`
        );
      }

      if (unavailableItems.skills.length > 0) {
        warnings.push(`${unavailableItems.skills.length} skills are from disabled rulebooks`);
      }

      if (unavailableItems.talents.length > 0) {
        warnings.push(`${unavailableItems.talents.length} talents are from disabled rulebooks`);
      }

      if (unavailableItems.equipment.length > 0) {
        warnings.push(
          `${unavailableItems.equipment.length} equipment items are from disabled rulebooks`
        );
      }

      if (warnings.length > 0) {
        suggestions.push(
          'Consider enabling the required rulebooks or removing the unavailable content'
        );
      }
    }

    return {
      isValid: !hasUnavailableContent,
      warnings,
      suggestions
    };
  }

  /**
   * Get recommended rulebooks for a character based on their content
   */
  public getRecommendedRulebooks(character: Character): string[] {
    const usedRulebooks = new Set<string>();

    // Collect all rulebooks used by character content
    if (character.career.rulebook) usedRulebooks.add(character.career.rulebook);
    if (character.homeworld.rulebook) usedRulebooks.add(character.homeworld.rulebook);
    if (character.birthright.rulebook) usedRulebooks.add(character.birthright.rulebook);
    if (character.lure.rulebook) usedRulebooks.add(character.lure.rulebook);
    if (character.trials.rulebook) usedRulebooks.add(character.trials.rulebook);
    if (character.motivation.rulebook) usedRulebooks.add(character.motivation.rulebook);

    character.skills.forEach((item) => usedRulebooks.add(item.rulebook));
    character.talents.forEach((item) => usedRulebooks.add(item.rulebook));
    character.traits.forEach((item) => usedRulebooks.add(item.rulebook));
    character.equipment.forEach((item) => usedRulebooks.add(item.rulebook));
    character.weapons.forEach((item) => usedRulebooks.add(item.rulebook));
    character.armor.forEach((item) => usedRulebooks.add(item.rulebook));

    return Array.from(usedRulebooks).filter(
      (rulebook) => !character.enabledRulebooks.includes(rulebook)
    );
  }
}

// Export singleton instance
export const characterRulebookService = CharacterRulebookService.getInstance();
